import { providerDispatchRuntimeContextSchema } from "@wakeloop/schemas";
import type { ProviderDispatchRuntimeContext } from "@wakeloop/schemas/types";
import { resolveCurrentWakeLoopAccountContext } from "../config/account-context";
import {
  normalizeWakeLoopProfileId,
  type WakeLoopAccountKey,
} from "../config/paths";
import {
  describeProfileWorkspacePathValidationFailure,
  validateCustomProfileWorkspacePath,
} from "./custom-path";
import {
  ensureWakeLoopManagedProfileWorkspace,
  readProfileWorkspaceConfigForAccount,
  resolveConfiguredProfileWorkspacePathForAccount,
} from "./service";

export type ProfileWorkspaceRuntimeResolution =
  | {
      configPath: string;
      configuredWorkspacePath: string;
      effectiveRuntimeWorkingDirectory: string;
      runtimeContext: ProviderDispatchRuntimeContext;
      status: "ready";
      workspaceMode: "wakeloop_managed" | "custom";
    }
  | {
      configPath: string;
      configuredWorkspacePath?: null;
      reasonCode: "workspace.missing";
      reasonText: string;
      status: "missing";
      workspaceMode?: null;
    }
  | {
      configPath: string;
      configuredWorkspacePath?: string | null;
      reasonCode: "workspace.invalid";
      reasonText: string;
      status: "invalid";
      workspaceMode?: "wakeloop_managed" | "custom" | null;
    };

export async function resolveProfileWorkspaceRuntime(input: {
  account?: WakeLoopAccountKey;
  wakeLoopProfileId: string;
  mode: "dispatch" | "inspect";
}): Promise<ProfileWorkspaceRuntimeResolution> {
  const account = await resolveAccountKey(input.account);
  return await resolveProfileWorkspaceRuntimeForAccount({
    account,
    wakeLoopProfileId: input.wakeLoopProfileId,
    mode: input.mode,
  });
}

export async function resolveProfileWorkspaceRuntimeForAccount(input: {
  account: WakeLoopAccountKey;
  wakeLoopProfileId: string;
  mode: "dispatch" | "inspect";
}): Promise<ProfileWorkspaceRuntimeResolution> {
  const wakeLoopProfileId = normalizeWakeLoopProfileId(input.wakeLoopProfileId);
  const workspaceConfig = await readProfileWorkspaceConfigForAccount({
    account: input.account,
    wakeLoopProfileId,
  });
  if (workspaceConfig.status === "missing") {
    return {
      configPath: workspaceConfig.path,
      configuredWorkspacePath: null,
      reasonCode: "workspace.missing",
      reasonText: `Profile workspace config is missing for ${wakeLoopProfileId} at ${workspaceConfig.path}.`,
      status: "missing",
      workspaceMode: null,
    };
  }
  if (workspaceConfig.status === "invalid") {
    return {
      configPath: workspaceConfig.path,
      configuredWorkspacePath: null,
      reasonCode: "workspace.invalid",
      reasonText: resolveInvalidConfigReasonText({
        wakeLoopProfileId,
        configPath: workspaceConfig.path,
        reason: workspaceConfig.reason,
      }),
      status: "invalid",
      workspaceMode: null,
    };
  }

  const configuredWorkspacePath =
    resolveConfiguredProfileWorkspacePathForAccount({
      account: input.account,
      wakeLoopProfileId,
      value: workspaceConfig.value,
    });

  if (workspaceConfig.value.mode === "custom") {
    const validation = await validateCustomProfileWorkspacePath(
      workspaceConfig.value.customWorkspacePath
    );
    if (validation.status === "invalid") {
      return {
        configPath: workspaceConfig.path,
        configuredWorkspacePath,
        reasonCode: "workspace.invalid",
        reasonText: `Profile workspace config for ${wakeLoopProfileId} points at an invalid custom path. ${describeProfileWorkspacePathValidationFailure(
          validation
        )}`,
        status: "invalid",
        workspaceMode: "custom",
      };
    }
    return buildReadyResolution({
      configPath: workspaceConfig.path,
      configuredWorkspacePath,
      effectiveRuntimeWorkingDirectory: validation.value,
      workspaceMode: "custom",
    });
  }

  if (input.mode === "dispatch") {
    const ensured = await ensureWakeLoopManagedProfileWorkspace({
      account: input.account,
      wakeLoopProfileId,
      updatedAt: workspaceConfig.updatedAt,
    });
    return buildReadyResolution({
      configPath: workspaceConfig.path,
      configuredWorkspacePath: ensured.workspacePath,
      effectiveRuntimeWorkingDirectory: ensured.workspacePath,
      workspaceMode: "wakeloop_managed",
    });
  }

  return buildReadyResolution({
    configPath: workspaceConfig.path,
    configuredWorkspacePath,
    effectiveRuntimeWorkingDirectory: configuredWorkspacePath,
    workspaceMode: "wakeloop_managed",
  });
}

function buildReadyResolution(input: {
  configPath: string;
  configuredWorkspacePath: string;
  effectiveRuntimeWorkingDirectory: string;
  workspaceMode: "wakeloop_managed" | "custom";
}): ProfileWorkspaceRuntimeResolution {
  const runtimeContext = providerDispatchRuntimeContextSchema.safeParse({
    providerRuntimeWorkingDirectory: input.effectiveRuntimeWorkingDirectory,
  });
  if (!runtimeContext.success) {
    return {
      configPath: input.configPath,
      configuredWorkspacePath: input.configuredWorkspacePath,
      reasonCode: "workspace.invalid",
      reasonText: `Profile workspace runtime context is invalid for ${input.effectiveRuntimeWorkingDirectory}.`,
      status: "invalid",
      workspaceMode: input.workspaceMode,
    };
  }
  return {
    configPath: input.configPath,
    configuredWorkspacePath: input.configuredWorkspacePath,
    effectiveRuntimeWorkingDirectory: input.effectiveRuntimeWorkingDirectory,
    runtimeContext: runtimeContext.data,
    status: "ready",
    workspaceMode: input.workspaceMode,
  };
}

function resolveInvalidConfigReasonText(input: {
  wakeLoopProfileId: string;
  configPath: string;
  reason: "invalid_json" | "invalid_schema" | "profile_id_mismatch";
}): string {
  switch (input.reason) {
    case "invalid_json":
      return `Profile workspace config for ${input.wakeLoopProfileId} is not valid JSON at ${input.configPath}.`;
    case "invalid_schema":
      return `Profile workspace config for ${input.wakeLoopProfileId} is invalid at ${input.configPath}.`;
    case "profile_id_mismatch":
      return `Profile workspace config for ${input.wakeLoopProfileId} does not match the stored profile id at ${input.configPath}.`;
    default:
      return `Profile workspace config for ${input.wakeLoopProfileId} is invalid at ${input.configPath}.`;
  }
}

async function resolveAccountKey(
  account: WakeLoopAccountKey | undefined
): Promise<WakeLoopAccountKey> {
  if (account) {
    return account;
  }
  const accountContext = await resolveCurrentWakeLoopAccountContext();
  return accountContext.key;
}
